/**
 * DemoSharedLog - Realistic Manager and Courier Views
 * 
 * Manager view: Compact console-like log with inline timestamps
 * Courier view: Order card + pinned manager instruction + collapsible history
 * 
 * GUARDRAIL: This component uses only static demo data.
 * No imports from hooks/, features/, or services/.
 */

import { MapPin, MessageSquare, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  getDemoFullConversation, 
  getDemoCourierView, 
  STATUS_COLORS,
  type DemoLogEntry 
} from "@/lib/demoData";
import { RoleIcon, type LogRole } from "@/components/ui/RoleIcon";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMemo } from "react";

function LogEntryRow({ entry }: { entry: DemoLogEntry }) {
  return (
    <div className="flex items-start gap-2 px-3 py-1.5">
      <span className="text-[11px] font-mono text-white/60 w-9 flex-shrink-0">
        {entry.timestamp}
      </span>
      <RoleIcon role={entry.role as LogRole} size="md" className="flex-shrink-0 mt-0.5" />
      <p className={cn(
        "text-[13px] leading-snug",
        entry.isHighlight && "text-emerald-400 font-medium",
        entry.isSystem && "text-white/50",
        !entry.isSystem && !entry.isHighlight && "text-white/80"
      )}>
        {entry.message}
      </p>
    </div>
  );
}

function ManagerViewPanel() {
  const { t } = useLanguage();
  const conversation = useMemo(() => getDemoFullConversation(t), [t]);
  
  // Show only 4 entries (removed system entry as per plan)
  const entriesToShow = conversation.filter(e => !e.isSystem);
  
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-black/20">
        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
        <span className="text-[11px] font-mono text-white/50">{t("landing.demo.managerView")}</span>
      </div>
      
      {/* Compact conversation log */}
      <div className="py-2">
        {entriesToShow.map((entry, i) => (
          <LogEntryRow key={`${entry.timestamp}-${i}`} entry={entry} />
        ))}
      </div>
    </div>
  );
}

function CourierViewPanel() {
  const { t } = useLanguage();
  const courierView = useMemo(() => getDemoCourierView(t), [t]);
  const { order, lastManagerMessage, historyCount } = courierView;
  
  const statusColors = STATUS_COLORS[order.status] || STATUS_COLORS["Ready"];
  const statusLabel = t(`landing.demo.statuses.${order.status}`) || order.status;

  return (
    <div className="bg-black/40 border border-white/10 rounded-lg overflow-hidden">
      {/* Header with pulse */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-black/20">
        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
        <span className="text-[11px] font-mono text-white/50">{t("landing.demo.courierView")}</span>
        <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-heartbeat ml-auto" />
      </div>
      
      {/* Order info card - compact */}
      <div className="p-3 border-b border-white/10">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-medium text-white text-sm">
            {order.customer_name}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusColors.bg} ${statusColors.text}`}>
            {statusLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-white/60 text-xs">
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <span>{order.address}</span>
        </div>
        <div className="flex items-center gap-2 mt-1.5 text-xs">
          <span className="text-white/80 font-medium">
            {order.total_price}₾
          </span>
          <span className="text-white/60">•</span>
          <span className="text-white/60">#{order.order_number}</span>
        </div>
      </div>
      
      {/* Pinned Manager Instruction - compact */}
      <div className="p-3">
        <div className="bg-primary/10 border border-primary/30 rounded-md p-2">
          <div className="flex items-start gap-2">
            <MessageSquare className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-white/90 leading-snug">
              {lastManagerMessage.message}
            </p>
          </div>
        </div>
      </div>
      
      {/* Collapsible History - just the button (collapsed state) */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-t border-white/5 bg-white/5">
        <ChevronRight className="h-3 w-3 text-white/60" />
        <span className="text-[10px] text-white/60">{t("landing.demo.history")} ({historyCount})</span>
      </div>
    </div>
  );
}

export function DemoSharedLog() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
      {/* Manager View - Wider desktop console (3/5) */}
      <div className="md:col-span-3">
        <ManagerViewPanel />
      </div>
      
      {/* Courier View - Narrower phone-sized (2/5) */}
      <div className="md:col-span-2">
        <CourierViewPanel />
      </div>
    </div>
  );
}